// Journal article bodies as structured blocks, rendered by src/pages/Article.tsx (no raw HTML).
// Edit the text freely; keep each article's h2 → h3 order (never jump from h2 straight to h4).

/** A list item: plain text, or a bold lead-in term followed by text. */
export type ListItem = string | { term: string; text: string };

export type ArticleBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: ListItem[] }
  | { type: "ol"; items: ListItem[] }
  | { type: "quote"; text: string };

const words = (s: string) => s.split(/\s+/).filter(Boolean).length;

/** Word count of an article body (used for JSON-LD wordCount). */
export function countWords(blocks: ArticleBlock[]): number {
  let n = 0;
  for (const b of blocks) {
    if (b.type === "ul" || b.type === "ol") {
      for (const item of b.items)
        n +=
          typeof item === "string"
            ? words(item)
            : words(item.term) + words(item.text);
    } else n += words(b.text);
  }
  return n;
}

/** URL-safe id for an h2, used for the in-page table of contents. */
export const headingId = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const articleBodies: Record<string, ArticleBlock[]> = {
  "5-things-that-take-a-room-from-good-to-great": [
    {
      type: "p",
      text: "Most rooms that feel “almost there” are not missing furniture. They are usually carrying too much of it, lit by a single row of ceiling lights that flattens every surface. The difference between a good room and a great one is rarely a bigger budget. It is a handful of deliberate decisions about what to leave out, and how light falls on what remains.",
    },
    {
      type: "p",
      text: "In Nagercoil, Kanyakumari and across Kerala, those decisions carry extra weight. Our daylight is intense, the air is humid for much of the year, and ceiling fans run almost constantly. A room that ignores those realities can look beautiful in a photograph and still feel cramped, glaring or stuffy to live in. Here are the five things we return to on every project.",
    },
    { type: "h2", text: "1. Treat empty space as a material" },
    {
      type: "p",
      text: "Negative space is the floor, wall and air you consciously choose not to fill. It gives the eye somewhere to rest and lets the pieces you love stand out. Practically, it also keeps air moving: furniture pushed tight against every wall and window blocks the cross-breeze that keeps a coastal home comfortable.",
    },
    {
      type: "ul",
      items: [
        "Leave at least one wall in each main room largely bare, so a single artwork, a textured finish or simply light can become the focus.",
        "Keep walkways clear and generous. A room you can move through without turning sideways instantly feels calmer.",
        "Float key furniture a little away from walls and windows so air can circulate behind it and moisture does not collect.",
      ],
    },
    {
      type: "p",
      text: "A useful test: take one item out of the room for a week. If nobody misses it, it was clutter dressed up as decoration.",
    },
    { type: "h2", text: "2. Light in layers, not in rows" },
    {
      type: "p",
      text: "The single biggest upgrade most homes can make is to stop relying on one grid of downlights. Great rooms are lit in three layers that can be switched independently.",
    },
    {
      type: "ul",
      items: [
        {
          term: "Ambient light",
          text: "for general visibility: cove lighting tucked into the edge of a false ceiling, or a few well-placed fittings rather than a dense grid.",
        },
        {
          term: "Task light",
          text: "where work happens: under-cabinet strips in the kitchen, a reading lamp beside the sofa, mirror lighting in the bathroom.",
        },
        {
          term: "Accent light",
          text: "that gives the room its character: a wall washer grazing a lime-plaster or stone wall, a picture light, or a concealed strip inside a niche.",
        },
      ],
    },
    {
      type: "p",
      text: "Grazing light is where the alchemy happens. A light source placed close to a textured wall and aimed along it reveals every ridge of fluted wood and every variation in Kota stone or hand-troweled plaster. The same wall lit head-on looks flat.",
    },
    { type: "h3", text: "Mind the ceiling fan" },
    {
      type: "p",
      text: "In our climate the ceiling fan is non-negotiable, and it interacts with lighting. Downlights placed above or close to the sweep of the blades cause a flickering, strobing shadow that is tiring to sit under. Fix the fan positions first, then place lights well clear of the blades, or lean more on wall and cove lighting in rooms where the fan sits at the centre.",
    },
    { type: "h3", text: "Keep colour temperature consistent" },
    {
      type: "p",
      text: "Mixing cool white and warm white fittings in one room is one of the quickest ways to make it feel unfinished. Most families are happiest with warm white in living and sleeping spaces, with neutral white reserved for kitchens and work areas. Whatever you choose, keep every fitting in a room the same, and put the ambient and accent circuits on dimmers so the room can shift from a bright afternoon to a calm evening.",
    },
    { type: "h2", text: "3. Edit the daylight, don’t just let it in" },
    {
      type: "p",
      text: "South Indian daylight is a gift and a challenge. Unfiltered west sun in the afternoon brings heat and glare that no lighting plan can fix. Great rooms manage daylight before it reaches the interior.",
    },
    {
      type: "ul",
      items: [
        "Hang sheer linen or cotton curtains behind heavier drapes, so you can soften glare without darkening the room.",
        "Consider wooden louvres, jaali screens or deep sunshades on west-facing openings. They break the light into a pattern that changes through the day.",
        "Avoid high-gloss surfaces directly opposite large windows, where they throw reflected glare back into the room.",
      ],
    },
    {
      type: "p",
      text: "Filtered light is also kinder to your materials. Teak, upholstery and artwork fade far more slowly when they are not sitting in direct sun for hours every day.",
    },
    { type: "h2", text: "4. Fewer materials, chosen honestly" },
    {
      type: "p",
      text: "A great room usually rests on a short, confident palette: three or four materials repeated with intention rather than ten competing for attention. Our region offers wonderful options that also suit the climate. Granite and Kota stone floors stay cool underfoot, teak and other well-seasoned hardwoods make lasting joinery, lime plaster copes with humidity better than many paints, and brass develops a warm patina over the years.",
    },
    {
      type: "p",
      text: "Honesty matters as much as restraint. Laminate printed to look like stone, or plastic made to imitate wood, tends to look tired quickly in humid air and under close-up light. Where budgets are tight, a plainer real material almost always ages better than an elaborate imitation.",
    },
    {
      type: "quote",
      text: "Luxury is not what you add to a room. It is the confidence to leave the right things out.",
    },
    { type: "h2", text: "5. Get the proportions — and the air — right" },
    {
      type: "p",
      text: "The last ingredient is invisible on a mood board but obvious the moment you walk in: proportion. Furniture should suit the size of the room and the height of the ceiling. An oversized sectional in a modest living room swallows the negative space you worked to create, while a small rug floating in a large hall makes everything feel disconnected.",
    },
    {
      type: "p",
      text: "In a hot, humid climate, proportion also includes air. Keep openings on opposite walls unobstructed wherever possible so breezes can cross the room. Choose raised or open-legged furniture where dampness is a concern, and resist filling every wall with full-height units. If your family follows Vastu, raise it at this stage too: the orientation of seating, the placement of heavy furniture and a lighter, more open north-east corner are far easier to plan for before joinery is built than after.",
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "ul",
      items: [
        "Remove before you add. One bare wall per room is a good start.",
        "Plan ambient, task and accent lighting on separate switches, and keep colour temperature consistent.",
        "Filter afternoon and west sun rather than fighting it with brighter lights.",
        "Choose a short palette of real, climate-suited materials.",
        "Protect cross-ventilation when you place furniture.",
      ],
    },
    {
      type: "p",
      text: "If you have a room that feels “almost there”, our Nagercoil studio would be happy to talk it through with you. A short consultation is often enough to find the two or three changes that will make the biggest difference.",
    },
  ],

  "functional-and-stylish-wall-to-wall-shelves": [
    {
      type: "p",
      text: "A wall of well-made shelves does far more than store things. It can replace a scattered collection of mismatched cabinets, give a room a strong architectural backbone, and become the place where the objects that tell your family’s story are displayed. Done badly, it becomes a dusty, sagging wall of clutter. The difference lies in planning, structure and, in a humid coastal climate like ours, the choice of materials.",
    },
    { type: "h2", text: "Start with what the wall needs to hold" },
    {
      type: "p",
      text: "Before sketching anything, list what will actually live on the shelves. Books, a television, puja items, heirloom brass and bronze, children’s toys, files and board games all have different needs. That list decides the depth of each shelf, the spacing between them and how much of the unit should be open or closed.",
    },
    {
      type: "ul",
      items: [
        {
          term: "Books:",
          text: "paperbacks need shallow shelves, while art books and photo albums need deeper ones. Matching depth to contents stops books being pushed to the back and forgotten.",
        },
        {
          term: "Display pieces:",
          text: "a few deeper niches with their own lighting give pottery, sculpture or a lamp the space it deserves.",
        },
        {
          term: "Everyday clutter:",
          text: "chargers, remotes, papers and medicines belong behind doors or in drawers, not on display.",
        },
      ],
    },
    {
      type: "p",
      text: "As a starting point, we usually design the lower section as closed storage, because that is where things collect and where small hands reach, and keep the open shelving higher up at eye level, where display pieces can be enjoyed.",
    },
    { type: "h2", text: "Structure: avoiding the sag" },
    {
      type: "p",
      text: "Long, unsupported shelves bend over time, especially once they are loaded with books. A few design moves prevent this without spoiling the clean look.",
    },
    {
      type: "ul",
      items: [
        "Keep individual spans modest by adding vertical dividers at regular intervals. The rhythm of those verticals often becomes the strongest feature of the design.",
        "Use thicker shelf boards, or a deeper front edge, on longer runs.",
        "Conceal a steel flat or angle inside the shelf where a long span is unavoidable.",
        "Fix the unit to the wall at several points, after checking for concealed electrical conduits and water lines.",
      ],
    },
    {
      type: "p",
      text: "Floor-to-ceiling units should always be anchored against tipping. This matters in any home with children who like to climb.",
    },
    { type: "h2", text: "Materials that survive humidity" },
    {
      type: "p",
      text: "Our climate is hard on joinery. Months of high humidity, monsoon dampness and, near the coast, salt-laden air can cause swelling, peeling veneers, fungal growth and rusted hardware. Material choices should reflect that from day one.",
    },
    { type: "h3", text: "Carcass and shelves" },
    {
      type: "p",
      text: "Good-quality boiling-water-resistant (BWR) or boiling-water-proof (BWP) plywood is a dependable choice for carcasses and shelves. Teak and other well-seasoned hardwoods remain excellent for exposed edges, frames and shutters, though solid timber moves with the seasons and needs details that allow for that movement. Ordinary particle board and untreated MDF are best kept away from external walls and wet areas, where they absorb moisture and swell.",
    },
    { type: "h3", text: "Seal every surface" },
    {
      type: "p",
      text: "Moisture enters through any unsealed face, including the back and the edges nobody sees. Every surface should be laminated, veneered and polished, or painted, not just the visible front. Termite-treated boards and a treated wall behind the unit add another layer of protection.",
    },
    { type: "h3", text: "Let the wall breathe" },
    {
      type: "p",
      text: "External walls can take on dampness during the monsoon, so leave a small ventilated gap between the wall and the back of the unit. A slightly raised plinth also keeps the base clear of water when floors are mopped.",
    },
    { type: "h3", text: "Choose the right hardware" },
    {
      type: "p",
      text: "Hinges, channels and handles should suit humid and, near the sea, salty air. Stainless steel or good-quality coated fittings last far longer than plain mild steel, which can rust and stain the surrounding wood.",
    },
    { type: "h2", text: "Open, closed and everything in between" },
    {
      type: "p",
      text: "Fully open shelving looks wonderful in photographs but gathers dust quickly, particularly in homes near busy roads or sandy coastal areas. A balance of open and closed sections usually works best.",
    },
    {
      type: "ul",
      items: [
        "Glass-fronted sections protect books and collectibles while keeping them on view.",
        "Cane, rattan or perforated panels let air into closed sections, which helps prevent musty smells in humid months.",
        "Sliding or pocket doors can hide a television or a compact home office when it is not in use.",
      ],
    },
    { type: "h2", text: "Lighting turns storage into a gallery" },
    {
      type: "p",
      text: "Integrated lighting is what lifts a wall of shelves from storage to architecture. Slim LED profiles recessed into the underside of each shelf, or vertical strips hidden in the dividers, wash the contents in soft light. A few things are worth planning early:",
    },
    {
      type: "ul",
      items: [
        "Route wiring through the carcass before it is assembled, rather than surface-mounting cables afterwards.",
        "Keep drivers and transformers behind a removable panel, so they can be replaced without dismantling the unit.",
        "Use warm white light with diffusers, so individual LED dots do not reflect in glossy objects.",
        "Give the shelving its own switch or dimmer, so it can serve as a gentle evening light on its own.",
      ],
    },
    { type: "h2", text: "Placement and Vastu" },
    {
      type: "p",
      text: "Wall-to-wall units are heavy and visually dominant, so their position matters. Where possible, choose a wall that is not interrupted by windows or doors and that does not block a cross-breeze. Families who follow Vastu often prefer heavy storage along the south or west walls, keeping the north-east lighter and more open. That preference is easy to accommodate if it is raised before the layout is fixed.",
    },
    { type: "h2", text: "Caring for your shelves" },
    {
      type: "p",
      text: "A little routine care keeps joinery looking new. Dust open shelves with a soft, dry cloth, wipe up spills promptly, and open closed cabinets now and then during the monsoon to let air circulate. A silica gel sachet or another moisture absorber in closed sections helps protect books and fabrics. If you notice a musty smell or a white bloom on a surface, act early; more airflow is usually the cure.",
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "p",
      text: "Plan the contents first, keep spans short, seal every surface, give the wall room to breathe and build in the lighting from the start. Those five decisions separate a tailored library wall that lasts for decades from a set of shelves that sags and swells within a few monsoons.",
    },
    {
      type: "p",
      text: "If you are considering a library wall, a media unit or full-height storage, we would be glad to look at your space with you. Share a photo or a floor plan with our studio and we can talk through what would suit your home.",
    },
  ],

  "the-5-secrets-to-pulling-off-simple-minimal-design": [
    {
      type: "p",
      text: "Minimal interiors promise calm: clear surfaces, a quiet palette and room to breathe. Yet many minimal rooms end up feeling cold, like a showroom nobody actually lives in. The problem is rarely minimalism itself. It is minimalism that removes warmth along with the clutter. Here are five principles we rely on to keep simple interiors inviting, adapted for the light, heat and humidity of South India.",
    },
    { type: "h2", text: "1. Let texture do the work that ornament used to do" },
    {
      type: "p",
      text: "When you take away decoration, the surfaces that remain have to carry the room. Flat, uniform finishes such as glossy laminate, plain emulsion and polished vitrified tiles leave a minimal room with nothing to hold the eye. Tactile, natural materials add depth without adding objects.",
    },
    {
      type: "ul",
      items: [
        "Lime plaster or textured mineral finishes on walls catch the light differently through the day.",
        "Kota stone, honed granite or terracotta floors feel grounded and stay cool underfoot.",
        "Teak or ash joinery in a matt finish brings warmth to cabinetry and doors.",
        "Handloom cotton, linen and cane add softness, and suit a humid climate better than heavy synthetics.",
      ],
    },
    {
      type: "p",
      text: "Matt and honed finishes are generally kinder in our climate too. They show fewer water marks, reflect less glare and age more gracefully than high-gloss surfaces.",
    },
    { type: "h2", text: "2. Warm the palette, and skip the stark white" },
    {
      type: "p",
      text: "Brilliant white walls can look striking under grey northern skies. Under intense South Indian sun they often turn harsh and glaring. A minimal palette works better when it is built from warm off-whites, soft lime, sand, clay and stone tones, with one or two deeper accents in wood, green or bronze. These colours still read as light and airy, but they hold their warmth in the evening when the lamps come on.",
    },
    {
      type: "p",
      text: "Limit the palette rather than the warmth. Three or four related tones used consistently look calmer than a “neutral” scheme made of many slightly different whites.",
    },
    { type: "h2", text: "3. Hidden storage is the backbone of minimalism" },
    {
      type: "p",
      text: "Minimalism fails the moment everyday life has nowhere to go. Indian homes hold a great deal: steel vessels and pressure cookers, puja items, school bags, festival decorations, guest bedding and years of documents. If storage is not planned generously, clutter returns within weeks.",
    },
    {
      type: "ul",
      items: [
        "Full-height wardrobes and cabinets with flush, handle-less shutters read as a calm wall rather than as furniture.",
        "Tall kitchen units with internal organisers keep counters genuinely clear.",
        "Built-in seating with storage underneath, and drawers beneath platform beds, use space that would otherwise be wasted.",
        "A small, well-organised utility or store room absorbs the things that do not belong in the living areas.",
      ],
    },
    {
      type: "p",
      text: "In humid months, closed storage needs ventilation, through discreet slots, a gap at the plinth or cane panels, so that what is hidden away does not develop damp or odour.",
    },
    { type: "h2", text: "4. Calibrate daylight like a material" },
    {
      type: "p",
      text: "Minimal rooms depend on light more than any other style, simply because there is so little else to look at. The aim is not maximum daylight but well-shaped daylight: soft, even and changing gently through the day.",
    },
    { type: "h3", text: "Borrow from traditional architecture" },
    {
      type: "p",
      text: "The traditional homes of Kerala and Tamil Nadu solved this beautifully with courtyards, deep verandahs, jaali screens and high clerestory openings. Many of those ideas translate directly into contemporary homes: a light-well over a staircase, a high window that lets light wash down a textured wall, or a perforated screen that casts shifting patterns across a plain floor.",
    },
    { type: "h3", text: "Then layer the artificial light" },
    {
      type: "p",
      text: "At night, recreate that softness with concealed cove lighting, wall washers and a few warm lamps at seating height, rather than a bright overhead grid. A minimal room lit only from the ceiling feels clinical; the same room lit low and from the sides feels intimate.",
    },
    { type: "h2", text: "5. Leave room for imperfection and personal things" },
    {
      type: "p",
      text: "The most sterile minimal interiors are the ones with no trace of the people who live in them. Warmth comes from objects with a history: a brass lamp from a grandparent’s home, a hand-thrown pot, a well-worn wooden chest, a few books that are actually being read. Edit them; don’t erase them.",
    },
    {
      type: "quote",
      text: "A minimal home is not an empty home. It is a home where every remaining object has earned its place.",
    },
    {
      type: "p",
      text: "Natural materials help here too. Timber develops a patina, stone shows gentle wear and linen softens with every wash. Rather than treating these changes as defects, a warm minimal interior welcomes them as part of the home’s character.",
    },
    { type: "h2", text: "Common mistakes to avoid" },
    {
      type: "ul",
      items: [
        "Specifying every surface in high gloss, which multiplies glare and shows every smudge.",
        "Cutting storage in pursuit of a clean look, then living with clutter on every counter.",
        "Using only cool white lighting, which makes neutral palettes feel cold.",
        "Choosing furniture that is all the same height and shape, so the room has no rhythm.",
        "Ignoring ventilation. Sealed, minimal rooms in our climate quickly feel stuffy.",
      ],
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "p",
      text: "Warm minimalism is subtraction done with care. Replace ornament with texture, choose a warm and limited palette, plan more storage than you think you need, shape the daylight thoughtfully and keep the objects that matter to you. The result is a home that feels calm without ever feeling cold.",
    },
    {
      type: "p",
      text: "If you are drawn to a simpler, quieter home but worry that it might feel bare, our team would be happy to walk through your space and ideas with you in a consultation.",
    },
  ],

  "9-unique-and-unusual-ways-to-display-your-tv": [
    {
      type: "p",
      text: "The television is often the largest object in a living room and, when it is switched off, the least attractive: a flat black rectangle that dominates an entire wall. Rather than designing the room around it, we prefer to design the wall so that the screen becomes part of a composition, or disappears entirely when it is not in use. Here are nine approaches we use, along with the practical details that matter in a warm, humid South Indian home.",
    },
    { type: "h2", text: "Before you choose: three practical checks" },
    {
      type: "ul",
      items: [
        {
          term: "Glare.",
          text: "Place the screen on a wall perpendicular to large windows rather than opposite them. Bright coastal daylight reflecting off a screen is a problem no cabinet can solve.",
        },
        {
          term: "Viewing height.",
          text: "The centre of the screen should sit close to seated eye level. Mounting it high above a console or a feature wall strains necks.",
        },
        {
          term: "Heat and air.",
          text: "Televisions, set-top boxes and amplifiers all produce heat. Equipment closed up in an unventilated box runs hot and humid in our climate, which shortens its life. Every concealed solution below needs airflow.",
        },
      ],
    },
    {
      type: "p",
      text: "Plan cable conduits, power points and surge protection before the walls are plastered. Monsoon thunderstorms and voltage fluctuations are a fact of life here, and good protection costs far less than replacing electronics.",
    },
    { type: "h2", text: "1. Sliding slatted panels" },
    {
      type: "p",
      text: "Vertical timber slats in teak, a walnut-toned veneer or a painted finish can be mounted on a concealed track and slid across to cover the screen. Open, the slats frame the television; closed, they read as a calm, textured wall. Leave gaps between the slats, or vents at the top and bottom, so warm air can escape.",
    },
    { type: "h2", text: "2. The gallery wall" },
    {
      type: "p",
      text: "Surround the screen with framed art, prints or family photographs in a carefully composed arrangement, and the dark rectangle becomes one frame among many. Black or dark wood frames help the screen blend in, and a consistent hanging grid keeps the wall orderly rather than busy.",
    },
    { type: "h2", text: "3. A frame-style surround" },
    {
      type: "p",
      text: "A deep timber or brass-finished frame built around a flush-mounted screen gives it the presence of a painting. Some televisions can also display artwork when idle, and combined with a frame the effect can be convincing. Near the sea, choose solid brass, stainless steel or well-coated finishes rather than thinly plated metal, which can pit and tarnish in salty air.",
    },
    { type: "h2", text: "4. A recessed niche" },
    {
      type: "p",
      text: "Setting the screen into a niche so that it sits flush with the surrounding wall is one of the cleanest solutions of all. It works best when it is planned during construction or built into a new false wall. Allow ventilation behind the niche, and keep an access panel for cables and devices.",
    },
    { type: "h2", text: "5. A fluted or panelled media wall" },
    {
      type: "p",
      text: "A full-width wall of fluted wood, stone cladding or textured panels turns the screen into a small part of a large, beautiful surface. Concealed storage below, with ventilated shutters for devices, keeps remotes, routers and consoles out of sight. Grazing light from above or below brings out the texture in the evening.",
    },
    { type: "h2", text: "6. Fabric baffles and magnetic covers" },
    {
      type: "p",
      text: "Where acoustics matter, fabric-wrapped panels can sit beside or over the screen, held by magnets so they lift away easily. They soften echo as well as hiding the screen. Choose fabrics and cores that tolerate humidity, and never let them block equipment vents.",
    },
    { type: "h2", text: "7. Bi-fold or pocket doors" },
    {
      type: "p",
      text: "A cabinet with doors that fold back or slide into side pockets lets the screen disappear completely. It is a classic solution for formal living rooms, where the family wants a television for occasional viewing but not as the room’s focal point. Specify good-quality hardware rated for frequent use, and keep the cabinet ventilated.",
    },
    { type: "h2", text: "8. A swivel mount on a partition" },
    {
      type: "p",
      text: "In open-plan homes, a television on a rotating pole or a partition between the living and dining areas can turn to face either space. It can save you a second screen and keeps the main living wall free for art or shelving. The partition needs solid internal framing and cabling concealed through its core.",
    },
    { type: "h2", text: "9. Projector and retractable screen" },
    {
      type: "p",
      text: "For dedicated media rooms, a ceiling-mounted projector with a screen that rolls up into a recessed pocket leaves the wall completely clear when not in use. Projectors need a darkened room, so plan blackout curtains and think about how the room will feel in daylight. In bedrooms, a motorised lift cabinet at the foot of the bed is a related option that keeps the screen out of sight.",
    },
    { type: "h2", text: "Vastu and placement" },
    {
      type: "p",
      text: "Families who follow Vastu often have a preferred wall for the television and for seating, and preferences vary between households and advisers. Agree on this before the media wall is designed, because conduits, niches and structural framing are difficult to move later.",
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "p",
      text: "Start with glare, viewing height and ventilation, then choose the concealment style that suits how often you watch and how formal the room is. Plan wiring, surge protection and access panels before plastering, and select hardware and metal finishes that can handle humid, salty air.",
    },
    {
      type: "p",
      text: "If you are planning a living room or a media wall, our studio can help you weigh these options against your layout and the way your family uses the space. Bring your floor plan to a consultation and we will sketch a few possibilities together.",
    },
  ],

  "biophilic-interiors-living-greenery-coastal-architecture": [
    {
      type: "p",
      text: "Biophilic design rests on a simple idea: people feel better when their surroundings connect them with nature, through daylight, fresh air, plants, water and natural materials. In much of the world this means bringing a little green indoors. Along the coasts of Kanyakumari and Kerala, where gardens grow almost effortlessly and the climate is warm all year, it can mean something more ambitious: interiors that blur into the landscape. Done well, it is beautiful and good for everyday wellbeing. Done carelessly, it brings damp, insects and endless maintenance. Here is how to get it right.",
    },
    { type: "h2", text: "Learn from the courtyard tradition" },
    {
      type: "p",
      text: "The traditional homes of this region already understood biophilic design. The Kerala nalukettu with its central nadumuttam, and the open courtyards of older Tamil Nadu homes, brought light, rain and air into the heart of the house. Warm air rose and escaped through the opening, while cooler air was drawn in through the shaded rooms around it.",
    },
    {
      type: "p",
      text: "A contemporary courtyard or atrium can do the same. Even a modest light-well over a staircase, with a few plants at its base, creates a vertical path for air and a daily connection with the sky.",
    },
    { type: "h3", text: "Open, covered or somewhere in between" },
    {
      type: "ul",
      items: [
        {
          term: "Open courtyards",
          text: "let in the rain and are the most authentic, but they need careful floor slopes, drainage and waterproofing of the rooms around them.",
        },
        {
          term: "Courtyards with a skylight or louvred roof",
          text: "stay dry during the monsoon while keeping the light. They must vent hot air at the top, or they will trap heat.",
        },
        {
          term: "Fully enclosed atriums",
          text: "suit air-conditioned homes, but rely more on mechanical ventilation and careful plant selection.",
        },
      ],
    },
    { type: "h2", text: "Water management: the part nobody photographs" },
    {
      type: "p",
      text: "Plants mean soil, and soil means water. Indoor planting fails most often not because of the plants, but because of what happens beneath them.",
    },
    {
      type: "ul",
      items: [
        "Built-in planters need a proper waterproof lining, a drainage layer below the soil and an outlet connected to a drain, not a sealed box that slowly becomes a swamp.",
        "Floors around planters and courtyards should slope away from the rooms, with stone or tiled edges that can take splashing.",
        "A root barrier protects walls and waterproofing from vigorous tropical roots.",
        "Avoid standing water in saucers, trays and decorative bowls. In our climate it quickly invites mosquitoes.",
      ],
    },
    {
      type: "p",
      text: "These details should be designed together with the civil and plumbing work, not added after the floor has been laid.",
    },
    { type: "h2", text: "Choosing plants for indoor coastal conditions" },
    {
      type: "p",
      text: "Light, more than watering, decides which plants succeed indoors. Watch how much light each spot really gets across a day before you choose.",
    },
    {
      type: "ul",
      items: [
        {
          term: "Bright courtyards and atriums:",
          text: "larger specimens such as ficus, areca palms and heliconias can thrive where there is strong, indirect light.",
        },
        {
          term: "Medium light near windows:",
          text: "philodendrons, monsteras, rubber plants and ferns are familiar, forgiving choices.",
        },
        {
          term: "Low-light corners:",
          text: "ZZ plants, snake plants, pothos and peace lilies tolerate shade better than most.",
        },
      ],
    },
    {
      type: "p",
      text: "Verandahs and balconies that face the sea take salt-laden wind, and hardy coastal species do far better there than delicate ornamentals. A good local nursery, and whatever is already flourishing in your neighbourhood, are usually the best guides.",
    },
    { type: "h2", text: "Microclimates and cross-ventilation" },
    {
      type: "p",
      text: "Plants and water add moisture to the air. In a climate that is already humid, that is only comfortable if the air keeps moving. Biophilic interiors here should be paired with generous cross-ventilation: openings on opposite sides of a room, high-level vents to release warm air, and ceiling fans positioned to keep air circulating around planted areas.",
    },
    {
      type: "p",
      text: "Shading matters just as much. Deep overhangs, pergolas with climbers and planted screens on the west side cool the air before it enters the house and reduce glare inside.",
    },
    { type: "h2", text: "Materials that belong beside greenery" },
    {
      type: "p",
      text: "Surfaces near plants and courtyards will be splashed, damp and occasionally muddy. Choose materials that tolerate it and look better with age: granite and Kota stone, terracotta, river pebbles, lime plaster and well-seasoned, properly finished hardwoods. Keep MDF, untreated plywood and wallpaper out of these zones, and near the sea use stainless or well-coated metal for railings, planters and fixings.",
    },
    { type: "h2", text: "Bringing nature in without plants" },
    {
      type: "p",
      text: "Not every home can, or should, support an indoor garden. Biophilic design also comes from framed views of trees, natural textures, the sound of rain falling into a courtyard, daylight that changes through the day, and patterns drawn from nature in screens, fabrics and stone. Simply turning a seating area towards a garden view can achieve a great deal with no maintenance at all.",
    },
    { type: "h2", text: "Planning for maintenance" },
    {
      type: "p",
      text: "A living interior needs care. Before committing, think about who will water, prune and clean, especially when the family travels. A simple drip irrigation line on a timer, planters you can reach easily, and a few larger, well-chosen plants instead of many small pots all make life easier.",
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "p",
      text: "Start with light and air, design drainage together with the building, choose plants for the light you actually have and surround them with materials that welcome moisture. Biophilic design in a coastal home is not about filling rooms with plants. It is about letting the landscape, the breeze and the daylight into the way you live.",
    },
    {
      type: "p",
      text: "If you are imagining a courtyard, an atrium or simply a greener way of living, we would be happy to explore the possibilities with you. A consultation with our studio is a good place to start.",
    },
  ],

  "natural-stones-marble-travertine-quartzite-comparison": [
    {
      type: "p",
      text: "Natural stone is one of the few materials that can genuinely last for generations. It is also one of the easiest to choose badly. A marble that looks perfect in the showroom can be etched by a squeeze of lemon within a week of moving in, while a quartzite that seems almost identical may shrug off the same treatment. Before you commit to a kitchen island, a vanity or a floor, it helps to understand what each stone actually is.",
    },
    { type: "h2", text: "Why geology matters" },
    {
      type: "p",
      text: "The three stones in this guide can look similar as polished samples, but they behave very differently because they form in different ways.",
    },
    {
      type: "ul",
      items: [
        {
          term: "Marble",
          text: "is limestone transformed by heat and pressure deep underground, made largely of calcite. It is prized for its veining and soft luminosity.",
        },
        {
          term: "Travertine",
          text: "is a form of limestone deposited by mineral springs, recognisable by its warm tones and natural pores.",
        },
        {
          term: "Quartzite",
          text: "is sandstone transformed by heat and pressure into a dense, crystalline rock made largely of quartz.",
        },
      ],
    },
    {
      type: "p",
      text: "The key difference is chemistry. Marble and travertine are calcium carbonate, which reacts with acids. Quartzite is quartz, which does not. That single fact explains most of what follows.",
    },
    { type: "h2", text: "Marble: beauty with a living surface" },
    {
      type: "p",
      text: "Marble’s appeal is its depth; light seems to enter the surface and glow back out. It is relatively soft and, importantly for Indian kitchens, sensitive to acid. Lemon juice, tamarind, tomato, vinegar and some cleaning products leave dull etch marks where they touch. Marble is also porous enough to absorb oil and turmeric if spills are left to sit.",
    },
    {
      type: "p",
      text: "None of this makes marble unsuitable; it has been used in homes for centuries. It does mean accepting patina. Over time a marble worktop gathers soft etches and marks that some people find beautiful and others find frustrating. Be honest with yourself about which group you belong to.",
    },
    { type: "h3", text: "Where marble shines" },
    {
      type: "ul",
      items: [
        "Living and dining floors, feature walls and table tops away from daily cooking.",
        "Bathroom vanities and wall cladding, with a sealed surface and gentle cleaners.",
        "Kitchen islands for families who welcome patina, ideally in a honed rather than polished finish, which disguises etching better.",
      ],
    },
    { type: "h2", text: "Travertine: warm, textured and forgiving to look at" },
    {
      type: "p",
      text: "Travertine brings a sun-baked warmth that suits relaxed, coastal interiors. Its natural holes are usually filled with resin or a cement-based filler at the factory; unfilled travertine has a more rustic texture that can trap grime in a kitchen. Chemically it behaves much like marble, so it etches with acids and benefits from sealing.",
    },
    {
      type: "p",
      text: "Its advantage is visual. The varied, matt surface hides marks well, so etching that would be obvious on polished white marble is far less noticeable. Travertine is lovely for floors, wall cladding, bathrooms and verandahs, where a honed or tumbled finish also gives better grip when wet.",
    },
    { type: "h3", text: "Where to be careful" },
    {
      type: "p",
      text: "On busy kitchen counters, the fills can wear or crack over time and may need occasional repair. On floors near entrances, sandy grit carried in from outside can dull the surface, so good doormats are worth having.",
    },
    { type: "h2", text: "Quartzite: the hard-working natural stone" },
    {
      type: "p",
      text: "Genuine quartzite is very hard, harder than glass, and it is not affected by kitchen acids. For a family that cooks with lemon, tamarind and tomato every day but wants a natural stone with marble-like veining, quartzite is often the answer. It still has some porosity, so a sealer is usually recommended, and it is harder to cut and finish, which affects fabrication time.",
    },
    { type: "h3", text: "A word of caution on labels" },
    {
      type: "p",
      text: "The trade does not always name stones consistently. Some slabs sold as “quartzite” or “soft quartzite” are really marble or dolomite, and they will etch. Always test a sample yourself before you buy the slab:",
    },
    {
      type: "ol",
      items: [
        {
          term: "The scratch test.",
          text: "Drag a sharp corner of the sample firmly across a piece of glass. True quartzite will scratch the glass; marble will not.",
        },
        {
          term: "The lemon test.",
          text: "Leave a few drops of lemon juice on the polished face for several minutes, then rinse. A dull spot means the stone contains calcite and will etch.",
        },
        {
          term: "The oil test.",
          text: "Leave a little cooking oil on the sample and see how quickly it darkens. This shows how much sealing the stone will need.",
        },
      ],
    },
    { type: "h2", text: "Don’t overlook the stones close to home" },
    {
      type: "p",
      text: "Homeowners in Tamil Nadu and Kerala have excellent, practical stones within easy reach. Granite, quarried widely across South India, is dense, hard and highly resistant to staining and etching, which makes it an outstanding worktop for heavy daily cooking. Kota stone, a fine-grained limestone, makes a cool, durable and understated floor; as a limestone it will etch, so it suits floors and verandahs better than kitchen counters. Both age gracefully in humid conditions.",
    },
    { type: "h2", text: "The finish changes everything" },
    {
      type: "p",
      text: "The same stone can behave quite differently depending on how its surface is finished.",
    },
    {
      type: "ul",
      items: [
        {
          term: "Polished:",
          text: "the richest colour and reflection, but it shows etching and scratches more and is slippery when wet. Avoid it on bathroom floors and monsoon-exposed thresholds.",
        },
        {
          term: "Honed:",
          text: "a smooth matt surface that hides wear and etching better. A sensible default for marble kitchens and for floors.",
        },
        {
          term: "Leathered or brushed:",
          text: "a soft, textured surface with good grip, popular on quartzite and granite worktops.",
        },
      ],
    },
    { type: "h2", text: "Caring for natural stone in a humid climate" },
    {
      type: "p",
      text: "Humidity does not harm stone in itself, but water and spills left on porous surfaces can leave marks. A few simple habits keep stone looking its best:",
    },
    {
      type: "ul",
      items: [
        "Wipe up acidic and oily spills promptly, especially on marble and travertine.",
        "Use pH-neutral stone cleaners, and avoid harsh acidic or bleach-based products.",
        "Reseal porous stones periodically, following the sealer manufacturer’s guidance. If a drop of water darkens the stone quickly, it is time.",
        "Use trivets under hot vessels and boards for chopping, even on the hardest stones.",
      ],
    },
    { type: "h2", text: "Choosing the right stone" },
    {
      type: "p",
      text: "Think about how each surface will really be used. For a hard-working kitchen with daily Indian cooking, granite or a properly tested quartzite are the safest natural choices. For a statement island where patina is welcome, honed marble is hard to beat. For warm, relaxed floors and bathrooms, travertine and Kota stone offer character and comfort underfoot.",
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "p",
      text: "Understand the chemistry, test every sample at home, choose a finish that suits the room, and seal and clean thoughtfully. Natural stone rewards that care with decades of service.",
    },
    {
      type: "p",
      text: "If you are deciding between stones for a kitchen, a bathroom or a floor, our team can help you compare samples and finishes against how your family actually lives. We would be glad to arrange a consultation.",
    },
  ],

  "acoustic-design-open-plan-homes": [
    {
      type: "p",
      text: "Open-plan living has transformed how we use our homes. Kitchen, dining and living spaces flow into one another, light travels further and the family stays connected. Yet many open-plan homes share one frustration: noise. Conversations echo, the television competes with the mixer grinder, and a gathering of a few friends suddenly feels loud. The cause is simple, a large volume of hard, reflective surfaces, and the solutions can be almost invisible.",
    },
    { type: "h2", text: "Why modern homes echo" },
    {
      type: "p",
      text: "Sound bounces off hard surfaces and loses very little energy as it does. Contemporary South Indian homes are full of them: vitrified or granite floors, plastered walls and ceilings, large glass windows and stone counters. These materials make sense in our climate, being cool, easy to clean and tolerant of humidity, but together they create a space where sound lingers. That lingering is called reverberation, and it is what makes a room feel noisy and speech harder to follow.",
    },
    {
      type: "p",
      text: "The goal is not to make the room dead or muffled. It is to absorb enough sound, in the right places, that conversation is easy and the room feels calm.",
    },
    { type: "h2", text: "Start with the ceiling" },
    {
      type: "p",
      text: "The ceiling is usually the largest uninterrupted surface in an open-plan room and the best place to begin, because treatment there never competes with furniture or art.",
    },
    {
      type: "ul",
      items: [
        {
          term: "Slatted or micro-perforated timber panels",
          text: "with an absorbent layer behind them combine warmth with acoustic performance, and work beautifully over living and dining zones.",
        },
        {
          term: "Acoustic plaster systems",
          text: "create a seamless ceiling that absorbs sound while looking like ordinary plaster, ideal for a minimal interior.",
        },
        {
          term: "A partial false ceiling",
          text: "over the dining or seating area can carry absorptive panels while also defining the zone and concealing lighting.",
        },
      ],
    },
    {
      type: "p",
      text: "In our climate, the absorbent material behind the surface matters. Mineral wool and polyester (PET) felt products generally cope with humidity better than many soft foams, which can break down or harbour mould in damp conditions. Ask for materials suited to humid environments, and make sure the ceiling void is ventilated.",
    },
    { type: "h2", text: "Walls that work quietly" },
    {
      type: "p",
      text: "After the ceiling, walls offer the next opportunity. Treat at least one large wall, especially one that faces another hard, parallel surface, where sound can bounce back and forth.",
    },
    {
      type: "ul",
      items: [
        "Fabric-wrapped panels in linen or wool blends can be designed as a feature behind the sofa or framed as an art wall.",
        "Full-height bookshelves are excellent at breaking up reflections; the irregular surface of books scatters sound.",
        "Timber slat walls with an absorbent backing bring the benefits of a slatted ceiling down to eye level.",
        "Heavy curtains across large windows absorb sound when drawn and soften the room visually.",
      ],
    },
    {
      type: "p",
      text: "Choose fabrics that can be vacuumed or removed for cleaning, and leave airflow behind wall panels so that they do not develop musty smells in humid months.",
    },
    { type: "h2", text: "Floors and furniture" },
    {
      type: "p",
      text: "Hard floors suit our climate, so rather than replacing them, add softness on top. A large rug under the seating area and another beneath the dining table make a noticeable difference. Cotton dhurries, jute and wool blends are practical choices; whichever you pick, lift and air rugs during the monsoon and keep them dry to prevent mould.",
    },
    {
      type: "p",
      text: "Upholstered sofas, cushions, fabric dining chairs and soft headboards all absorb sound. A room furnished only in leather, cane and stone will always be livelier than one with a generous mix of textiles.",
    },
    { type: "h2", text: "Zoning without walls" },
    {
      type: "p",
      text: "Acoustic comfort also comes from layout. Keep the television away from the kitchen, place the dining table between the cooking and seating areas, and use partial screens such as jaali panels, open shelving or slatted partitions to break up long sound paths without closing the space. A glass sliding partition between kitchen and living room is another option: open most of the day, closed while frying, grinding or entertaining.",
    },
    { type: "h2", text: "Controlling noise from outside, and from machines" },
    {
      type: "p",
      text: "Reverberation is only part of the picture. Many homes also contend with traffic, neighbours and, during the monsoon, heavy rain drumming on sheet roofs and balcony covers.",
    },
    {
      type: "ul",
      items: [
        "Well-sealed windows with good gaskets keep out much more noise than loose sliding frames; laminated or double glazing helps further on busy roads.",
        "Insulation beneath metal roofs reduces rain noise as well as heat.",
        "Choose quieter ceiling fans and air conditioners, and mount outdoor units on anti-vibration pads away from bedroom walls.",
        "Specify soft-close hardware for kitchen drawers and cabinet doors.",
      ],
    },
    { type: "h2", text: "Keeping it invisible" },
    {
      type: "p",
      text: "The best acoustic design is felt rather than seen. Absorptive ceilings can be finished to match plaster, fabric panels can be framed as art and slatted timber can become the room’s defining feature. Integrating these elements from the start, alongside the lighting and air-conditioning plan, avoids the look of panels added as an afterthought.",
    },
    { type: "h2", text: "The takeaway" },
    {
      type: "p",
      text: "Begin with the ceiling, treat one major wall, add rugs and textiles, zone the layout and seal the room against outside noise. Choose humidity-tolerant acoustic materials and keep air moving behind them. You keep the openness and the light, and lose the echo.",
    },
    {
      type: "p",
      text: "If your open-plan home feels louder than it should, our studio can review the space with you and suggest practical, discreet improvements. Get in touch to arrange a consultation.",
    },
  ],
};
